import { Link } from "react-router-dom";
import { MessageCircle, ShieldCheck, BadgeCheck, Coins, Search, ArrowRight, AlertTriangle } from "lucide-react";
import { useSettings } from "@/hooks/useSettings";
import { whatsappLink } from "@/lib/api";

const steps = [
  { icon: Search, title: "Find a Reseller", text: "Open the Resellers page and choose a verified reseller from your country." },
  { icon: MessageCircle, title: "Tap WhatsApp", text: "Press the green WhatsApp button to open a direct chat instantly." },
  { icon: Coins, title: "Request Coins", text: "Tell the reseller your Golden Live ID and how many coins you want." },
  { icon: BadgeCheck, title: "Get Recharged", text: "Complete payment and your coins are added to your Golden Live account." },
];

export default function Recharge() {
  const s = useSettings();
  const support = s.support_whatsapp
    ? whatsappLink("", s.support_whatsapp, s.whatsapp_message || "Hello, I need help with recharge.")
    : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-12">
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Fast & Secure</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold text-white sm:text-5xl">Recharge via Reseller</h1>
        <p className="mx-auto mt-3 max-w-xl text-slate-400">
          Recharge your Golden Live coins in seconds through our official resellers — no card, no hassle.
        </p>
        <Link to="/resellers" data-testid="recharge-browse-btn"
          className="mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-full gold-gradient px-8 text-base font-bold text-obsidian transition-transform hover:scale-[1.03]">
          Browse Resellers <ArrowRight className="h-5 w-5" />
        </Link>
      </div>

      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((st, i) => (
          <div key={i} className="card-surface relative overflow-hidden rounded-2xl p-6" data-testid={`recharge-step-${i + 1}`}>
            <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald/70 to-gold/50" />
            <span className="absolute right-4 top-4 font-display text-3xl font-bold text-gold/20">{i + 1}</span>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl gold-gradient">
              <st.icon className="h-6 w-6 text-obsidian" />
            </span>
            <h3 className="mt-4 text-lg font-bold text-slate-100">{st.title}</h3>
            <p className="mt-2 text-sm text-slate-400">{st.text}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 grid gap-5 md:grid-cols-2">
        <div className="card-surface rounded-2xl p-6">
          <div className="flex items-center gap-2 text-gold">
            <ShieldCheck className="h-5 w-5" />
            <h3 className="text-lg font-bold">Safety Tips</h3>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-slate-300">
            <li className="flex gap-2"><BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> Only recharge through resellers listed on this website.</li>
            <li className="flex gap-2"><BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> Never share your account password with anyone.</li>
            <li className="flex gap-2"><BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> Confirm the coin amount before paying.</li>
          </ul>
        </div>
        <div className="card-surface rounded-2xl border-amber-500/30 p-6">
          <div className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="h-5 w-5" />
            <h3 className="text-lg font-bold">Beware of Scams</h3>
          </div>
          <p className="mt-4 text-sm text-slate-300">
            Golden Live is not responsible for any transaction made with unlisted or fake resellers.
            Always verify the reseller ID matches this website before sending any payment.
          </p>
          {support && (
            <a href={support} target="_blank" rel="noreferrer" data-testid="recharge-support-btn"
              className="mt-5 inline-flex h-11 items-center gap-2 rounded-xl bg-whatsapp px-5 font-bold text-white hover:bg-whatsapp-hover">
              <MessageCircle className="h-5 w-5" /> Contact Support
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
